/* eslint-disable */
import * as PIXI from 'pixi.js';
import { ModelType } from './types/models';
import { Layout } from './types/Layout';
import { getLocalized, getScreenSize } from '../initPopulate';
import { setHitArea } from './setHitArea';
import cloneDeep from 'lodash/cloneDeep';
import { fixTextStyleV8 } from './fixTextStyleV8';
import { setTextureWithLoadingFallback } from '../../propsMixin/dynamicTextureLoading';
import { applyLocalizations } from './copyTextProps';

const isDefined = (value: unknown) => value !== undefined;

const setCommonProps = (view: any, state: any, values: any) => {
  isDefined(state.x) && (view.x = values.x);
  isDefined(state.y) && (view.y = values.y);
  isDefined(state.scale?.x) && (view.scale.x = values.scale.x);
  isDefined(state.scale?.y) && (view.scale.y = values.scale.y);
  isDefined(state.pivot?.x) && (view.pivot.x = values.pivot.x);
  isDefined(state.pivot?.y) && (view.pivot.y = values.pivot.y);
  isDefined(state.anchor?.x) && (view.anchor.x = values.anchor.x);
  isDefined(state.anchor?.y) && (view.anchor.y = values.anchor.y);
  isDefined(state.angle) && (view.angle = values.angle);
  isDefined(state.alpha) && (view.alpha = values.alpha);
  isDefined(state.visible) && (view.visible = values.visible);
  // TODO handle eventMode mess
  // if (isDefined(state.interactive)) {
  //   view.eventMode = values.interactive ? 'static' : 'none';
  // }
  // if (isDefined(state.buttonMode)) {
  //   view.cursor = values.buttonMode ? 'pointer' : 'default';
  // }
  isDefined(state.tint) && (view.tint = parseInt(values.tint));
  isDefined(state.blendMode) && (view.blendMode = values.blendMode);
  isDefined(state.hitArea) && setHitArea(view, values.hitArea);
  isDefined(state.texture) && setTextureWithLoadingFallback(view, values.texture);
};

const setNineSliceProps = (view: any, state: any, values: any) => {
  isDefined(state.leftWidth) && (view.leftWidth = values.leftWidth);
  isDefined(state.rightWidth) && (view.rightWidth = values.rightWidth);
  isDefined(state.topHeight) && (view.topHeight = values.topHeight);
  isDefined(state.bottomHeight) && (view.bottomHeight = values.bottomHeight);
  isDefined(state.width) && (view.width = values.width);
  isDefined(state.height) && (view.height = values.height);
};

const setTextProps = (view: any, state: any, model: any, values: any) => {
  if (isDefined(state.text)) {
    const localize = values.localize ?? model.localize;
    const mergedValues = { ...model, ...values };

    if (mergedValues.localize) {
      applyLocalizations(view, mergedValues);
    } else {
      view.text = localize ? getLocalized(values.text) : values.text;
    }
  }

  if (isDefined(state.style)) {
    const style = cloneDeep({ ...model.style, ...values.style });
    fixTextStyleV8(style);
    view.style = null;
    view.style = new PIXI.TextStyle(style);
  }
};

// Assigns state values for properties that are defined by the specified stateName
export const applyLayoutState = <T extends Layout>(layout: T, stateName: string) => {
  setModelsState(layout, stateName, false);
};

// Assigns defaults for properties that are defined by the specified stateName
export const revokeLayoutState = <T extends Layout>(layout: T, stateName: string) => {
  setModelsState(layout, stateName, true);
};

// Assigns defaults to all properties. Shouldn't be used often, as it's easy to break something
export const resetLayout = (layout: any) => {
  setModelsState(layout, null, false);
};

const setModelsState = (layout: any, stateName: string | null, revoke: boolean) => {
  const { raw } = layout.constructor;

  for (const model of raw.list) {
    const view = layout._viewById[model.id];
    setModelState(view, model, stateName, revoke);
  }

  setRootPosition(layout, stateName, revoke);
};

const setModelState = (view: any, model: any, stateName: string | null, revoke: boolean) => {
  if (!view) return;
  if (view.destroyed) return;
  if (!model.stateByName) return;

  const state = stateName === null ? model : model.stateByName[stateName] ?? {};
  const values = revoke ? model : state;

  if (model.type === ModelType.Container) {
    setCommonProps(view, state, values);
  } else if (model.type === ModelType.Sprite) {
    setCommonProps(view, state, values);
  } else if (model.type === ModelType.Text) {
    setCommonProps(view, state, values);
    setTextProps(view, state, model, values);
  } else if (model.type === ModelType.NineSlicePlane) {
    setCommonProps(view, state, values);
    setNineSliceProps(view, state, values);
  } else if (model.type === ModelType.Graphics) {
    setCommonProps(view, state, values);
  } else if (model.type === ModelType.Layout) {
    setCommonProps(view, state, values);
  }
};

const setRootPosition = (layout: any, stateName: string | null, revoke: boolean) => {
  const { raw } = layout.constructor;
  if (!raw.list?.length) return;
  const rootModel = raw.list[0];
  const view = layout._viewById[rootModel.id];
  const state = stateName === null ? rootModel : rootModel?.stateByName?.[stateName] ?? {};
  const values = revoke ? rootModel : state;
  const percentX = raw.x;
  const percentY = raw.y;

  if (isDefined(state.x)) {
    view.x = values.x + (percentX * getScreenSize().width) / 100;
  }

  if (isDefined(state.y)) {
    view.y = values.y + (percentY * getScreenSize().height) / 100;
  }
};
